import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDisplayDate } from '../../../utils/dateUtils';

const ROW_TYPE_CONFIG = {
  deliverable: {
    label: 'Deliverable',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: 'Package',
    indent: 0
  },
  task: {
    label: 'Task',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: 'CheckSquare',
    indent: 1
  },
  subtask: {
    label: 'Sub-task',
    badge: 'bg-green-100 text-green-800 border-green-200',
    icon: 'CornerDownRight',
    indent: 2
  }
};

const DeliverableRow = ({ deliverable, resources, allDeliverables, onEdit, onDelete, onUpdate, onAddChild, onDragStart, onDragOver, onDrop, isDragOver }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(deliverable?.name || '');
  const [editingOrder, setEditingOrder] = useState(false);
  const [orderValue, setOrderValue] = useState('');
  const nameInputRef = useRef(null);
  const orderInputRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    setNameValue(deliverable?.name || '');
  }, [deliverable?.name]);

  useEffect(() => {
    if (editingName && nameInputRef?.current) {
      nameInputRef?.current?.focus();
      nameInputRef?.current?.select();
    }
  }, [editingName]);

  useEffect(() => {
    if (editingOrder && orderInputRef?.current) {
      orderInputRef?.current?.focus();
      orderInputRef?.current?.select();
    }
  }, [editingOrder]);

  const handleNameSave = async () => {
    const trimmed = nameValue?.trim();
    if (!trimmed || trimmed === deliverable?.name) {
      setEditingName(false);
      setNameValue(deliverable?.name || '');
      return;
    }
    try {
      await onUpdate(deliverable?.id, { name: trimmed });
    } catch (err) {
      console.error('Error updating name:', err);
    }
    setEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); handleNameSave(); }
    if (e?.key === 'Escape') { setEditingName(false); setNameValue(deliverable?.name || ''); }
  };

  const handleOrderEdit = () => {
    setOrderValue(deliverable?.customOrder != null ? String(deliverable?.customOrder) : '');
    setEditingOrder(true);
  };

  const handleOrderSave = async () => {
    const val = parseFloat(orderValue);
    if (isNaN(val)) {
      setEditingOrder(false);
      return;
    }
    try {
      await onUpdate(deliverable?.id, { customOrder: val });
    } catch (err) {
      console.error('Error updating order:', err);
    }
    setEditingOrder(false);
  };

  const handleOrderKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); handleOrderSave(); }
    if (e?.key === 'Escape') { setEditingOrder(false); }
  };

  const formatOrderDisplay = (item) => {
    if (item?.customOrder == null) return '—';
    const n = Number(item?.customOrder);
    // Show as integer if whole number
    if (Number.isInteger(n)) return String(n);
    // Split into integer and decimal parts
    const parts = n?.toFixed(10)?.replace(/0+$/, '')?.split('.');
    const intPart = parts?.[0];
    const decPart = parts?.[1] || '';
    // Subtask: decimal has 2+ digits → format as X.Y.Z (insert dot after first decimal digit)
    if (decPart?.length >= 2) {
      return `${intPart}.${decPart?.[0]}.${decPart?.slice(1)}`;
    }
    // Task: single decimal digit → X.Y
    return `${intPart}.${decPart}`;
  };

  const rowType = deliverable?.rowType || 'deliverable';
  const typeConfig = ROW_TYPE_CONFIG?.[rowType] || ROW_TYPE_CONFIG?.deliverable;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'blocked': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-700';
      case 'high': return 'text-red-600';
      case 'medium': return 'text-orange-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return formatDisplayDate(dateString, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDependencyNames = () => {
    if (!deliverable?.dependsOn || deliverable?.dependsOn?.length === 0) return [];
    return deliverable?.dependsOn
      ?.map(depId => allDeliverables?.find(d => d?.id === depId)?.name)
      ?.filter(Boolean);
  };

  const handleStatusChange = async (newStatus) => {
    try { await onUpdate(deliverable?.id, { status: newStatus }); }
    catch (err) { console.error('Error updating status:', err); }
  };

  const dependencyNames = getDependencyNames();
  const indentClass = typeConfig?.indent === 1 ? 'ml-6' : typeConfig?.indent === 2 ? 'ml-12' : '';

  return (
    <div
      ref={dragRef}
      draggable
      onDragStart={(e) => onDragStart?.(e, deliverable)}
      onDragOver={(e) => { e?.preventDefault(); onDragOver?.(e, deliverable); }}
      onDrop={(e) => onDrop?.(e, deliverable)}
      className={`bg-card border rounded-lg p-4 hover:shadow-md transition-shadow ${indentClass} ${
        isDragOver ? 'border-primary border-2 bg-primary/5' : 'border-border'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Drag handle */}
        <div className="flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
          <Icon name="GripVertical" size={16} />
        </div>

        {/* Indent indicator for tasks/subtasks */}
        {rowType !== 'deliverable' && (
          <div className="flex-shrink-0 flex items-center mt-1">
            <div className={`w-0.5 h-full min-h-[20px] rounded ${
              rowType === 'task' ? 'bg-blue-300' : 'bg-green-300'
            }`} />
          </div>
        )}

        {/* Status Checkbox */}
        <button
          onClick={() => handleStatusChange(deliverable?.status === 'completed' ? 'in-progress' : 'completed')}
          className="flex-shrink-0 mt-1"
        >
          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
            deliverable?.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-blue-500'
          }`}>
            {deliverable?.status === 'completed' && <Icon name="Check" size={16} color="#FFFFFF" />}
          </div>
        </button>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {/* Type Badge */}
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${typeConfig?.badge}`}>
                  <Icon name={typeConfig?.icon} size={11} />
                  {typeConfig?.label}
                </span>

                {/* Order Badge */}
                <div className="flex items-center gap-1 group/order">
                  {editingOrder ? (
                    <input
                      ref={orderInputRef}
                      type="number"
                      step="0.001"
                      value={orderValue}
                      onChange={(e) => setOrderValue(e?.target?.value)}
                      onBlur={handleOrderSave}
                      onKeyDown={handleOrderKeyDown}
                      className="w-20 text-xs font-mono font-medium text-foreground bg-background border border-primary rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  ) : (
                    <button
                      onClick={handleOrderEdit}
                      title="Click to set order"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted border border-border text-xs font-mono font-medium text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
                    >
                      {formatOrderDisplay(deliverable)}
                    </button>
                  )}
                </div>

                {/* Inline name edit */}
                {editingName ? (
                  <input
                    ref={nameInputRef}
                    value={nameValue}
                    onChange={(e) => setNameValue(e?.target?.value)}
                    onBlur={handleNameSave}
                    onKeyDown={handleNameKeyDown}
                    className="text-base font-medium text-foreground bg-background border border-primary rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary min-w-0 flex-1"
                    style={{ maxWidth: '320px' }}
                  />
                ) : (
                  <div className="flex items-center gap-1 group/name">
                    <h4
                      className="text-base font-medium text-foreground cursor-pointer hover:text-primary transition-colors"
                      onClick={() => setEditingName(true)}
                      title="Click to edit name"
                    >
                      {deliverable?.name}
                    </h4>
                    <button
                      onClick={() => setEditingName(true)}
                      className="opacity-0 group-hover/name:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                      title="Edit name"
                    >
                      <Icon name="Pencil" size={12} />
                    </button>
                  </div>
                )}
              </div>
              {deliverable?.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">{deliverable?.description}</p>
              )}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Add child button */}
              {rowType === 'deliverable' && (
                <Button variant="ghost" size="icon" onClick={() => onAddChild(deliverable, 'task')} title="Add Task">
                  <Icon name="Plus" size={14} className="text-blue-600" />
                </Button>
              )}
              {rowType === 'task' && (
                <Button variant="ghost" size="icon" onClick={() => onAddChild(deliverable, 'subtask')} title="Add Sub-task">
                  <Icon name="Plus" size={14} className="text-green-600" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => onEdit(deliverable)}>
                <Icon name="Edit2" size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(deliverable?.id)}>
                <Icon name="Trash2" size={16} className="text-red-600" />
              </Button>
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{formatDate(deliverable?.startDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CalendarCheck" size={14} className="text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{formatDate(deliverable?.endDate)}</span>
            </div>
            {(deliverable?.estimatedHours || deliverable?.workEstimate) && (
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {deliverable?.workEstimate || `${deliverable?.estimatedHours}h`}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={14} className={getPriorityColor(deliverable?.priority)} />
              <span className={`text-xs font-medium ${getPriorityColor(deliverable?.priority)}`}>
                {deliverable?.priority?.charAt(0)?.toUpperCase() + deliverable?.priority?.slice(1)}
              </span>
            </div>
          </div>

          {/* Status and Resources */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(deliverable?.status)}`}>
                {deliverable?.status === 'pending' ? 'Not Started' : deliverable?.status?.charAt(0)?.toUpperCase() + deliverable?.status?.slice(1)?.replace('-', ' ')}
              </span>

              {deliverable?.assignedResources?.length > 0 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <div className="flex -space-x-2">
                    {deliverable?.assignedResources?.slice(0, 3)?.map((resource, index) => (
                      <div key={index} className="w-7 h-7 rounded-full border-2 border-card overflow-hidden bg-muted" title={resource?.name}>
                        <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground text-xs font-medium">
                          {resource?.name?.charAt(0)}
                        </div>
                      </div>
                    ))}
                    {deliverable?.assignedResources?.length > 3 && (
                      <div className="w-7 h-7 rounded-full border-2 border-card bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
                        +{deliverable?.assignedResources?.length - 3}
                      </div>
                    )}
                  </div>
                  <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                </button>
              )}

              {dependencyNames?.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="GitBranch" size={14} />
                  <span>{dependencyNames?.length} dep{dependencyNames?.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            {deliverable?.actualHours > 0 && (
              <div className="text-sm text-muted-foreground">
                <span>{deliverable?.actualHours}h / {deliverable?.estimatedHours}h</span>
              </div>
            )}
          </div>

          {/* Expanded Details */}
          {expanded && (
            <div className="mt-4 pt-4 border-t border-border space-y-4">
              {deliverable?.assignedResources?.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-foreground mb-3">Assigned Resources</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {deliverable?.assignedResources?.map((resource, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 bg-muted rounded-lg">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-background flex-shrink-0">
                          <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground font-medium text-sm">
                            {resource?.name?.charAt(0)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{resource?.name}</p>
                          <p className="text-xs text-muted-foreground">{resource?.category} • {resource?.identifier}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {dependencyNames?.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-foreground mb-3">Dependencies</h5>
                  <div className="flex flex-wrap gap-2">
                    {dependencyNames?.map((name, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1 bg-muted rounded-full">
                        <Icon name="GitBranch" size={12} className="text-muted-foreground" />
                        <span className="text-xs text-foreground">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliverableRow;